import { memo } from 'react';
import { Archive, Eye, MoreVertical, RotateCcw, ScrollText, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/shadcn/dropdown-menu';
import type { Popup } from '../../models/Popup';

interface DropdownActionsProps {
  popup: Popup;
  onPreview: (popup: Popup) => void;
  onViewEvents: (popup: Popup) => void;
  onArchive: (popup: Popup) => void;
  onRestore: (popup: Popup) => void;
  onDelete: (popup: Popup) => void;
}

export const DropdownActions = memo(function DropdownActions({
  popup,
  onPreview,
  onViewEvents,
  onArchive,
  onRestore,
  onDelete,
}: DropdownActionsProps) {
  const { t } = useTranslation('popups');
  const isArchived = popup.status === 'archived';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`popup-actions-${popup.id}`}
          aria-label={t('table.actionsMenu')}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" data-testid={`popup-actions-menu-${popup.id}`}>
        <DropdownMenuItem onClick={() => onPreview(popup)} data-testid={`preview-popup-${popup.id}`}>
          <Eye className="mr-2 h-4 w-4" />
          {t('actions.preview')}
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => onViewEvents(popup)} data-testid={`view-popup-events-${popup.id}`}>
          <ScrollText className="mr-2 h-4 w-4" />
          {t('actions.viewEvents')}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {isArchived ? (
          <DropdownMenuItem onClick={() => onRestore(popup)} data-testid={`restore-popup-${popup.id}`}>
            <RotateCcw className="mr-2 h-4 w-4" />
            {t('actions.restore')}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => onArchive(popup)} data-testid={`archive-popup-${popup.id}`}>
            <Archive className="mr-2 h-4 w-4" />
            {t('actions.archive')}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => onDelete(popup)}
          className="text-destructive focus:text-destructive"
          data-testid={`delete-popup-${popup.id}`}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {t('actions.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
